/**
 * Web NotificationService — extends @splitfiesta/core's pure NotificationService
 * with icon rendering helpers that depend on lucide-react and Tailwind classes.
 *
 * Mobile builds its own icon helpers using lucide-react-native (or NativeWind
 * variants) and imports the pure parts directly from @splitfiesta/core.
 */
import {
  AlertCircle,
  Bell,
  CheckCircle,
  CirclePause,
  Clock,
  DollarSign,
  Edit,
  Gem,
  HandshakeIcon,
  Heart,
  MessageCircle,
  ReceiptTextIcon,
  Repeat,
  Trash2,
  UserMinus,
  UserPlus,
  UserX,
  Users,
  XCircle,
} from 'lucide-react';

import React from 'react';

import { NotificationService as CoreNotificationService } from '@splitfiesta/core';

export class NotificationService extends CoreNotificationService {
  static getNotificationIcon(type: string): React.ReactElement {
    switch (type) {
      case 'expense_created':
        return React.createElement(DollarSign, { className: 'h-5 w-5' });
      case 'expense_updated':
        return React.createElement(Edit, { className: 'h-5 w-5' });
      case 'expense_deleted':
        return React.createElement(Trash2, { className: 'h-5 w-5' });
      case 'expense_comment':
        return React.createElement(MessageCircle, { className: 'h-5 w-5' });
      case 'expense_reaction':
        return React.createElement(Heart, { className: 'h-5 w-5' });
      case 'expense_recurring_created':
        return React.createElement(Repeat, { className: 'h-5 w-5' });
      case 'expense_recurring_paused':
        return React.createElement(CirclePause, { className: 'h-5 w-5' });
      case 'expense_pick_items':
        return React.createElement(ReceiptTextIcon, { className: 'h-5 w-5' });

      case 'settlement_created':
        return React.createElement(HandshakeIcon, { className: 'h-5 w-5' });
      case 'settlement_confirmed':
        return React.createElement(CheckCircle, { className: 'h-5 w-5' });
      case 'settlement_rejected':
      case 'settlement_cancelled':
        return React.createElement(XCircle, { className: 'h-5 w-5' });

      case 'group_created':
        return React.createElement(Users, { className: 'h-5 w-5' });
      case 'group_updated':
        return React.createElement(Edit, { className: 'h-5 w-5' });
      case 'group_deleted':
        return React.createElement(Trash2, { className: 'h-5 w-5' });
      case 'group_member_joined':
        return React.createElement(UserPlus, { className: 'h-5 w-5' });
      case 'group_member_left':
        return React.createElement(UserMinus, { className: 'h-5 w-5' });
      case 'group_member_removed':
        return React.createElement(UserX, { className: 'h-5 w-5' });
      case 'group_pro_added':
        return React.createElement(Gem, { className: 'h-5 w-5' });

      case 'payment_reminder':
        return React.createElement(AlertCircle, { className: 'h-5 w-5' });
      case 'expense_reminder':
        return React.createElement(Clock, { className: 'h-5 w-5' });

      case 'system':
        return React.createElement(Bell, { className: 'h-5 w-5' });

      default:
        return React.createElement(Bell, { className: 'h-5 w-5' });
    }
  }

  static getNotificationIconColor(type: string): string {
    switch (type) {
      case 'expense_created':
      case 'expense_recurring_created':
      case 'settlement_created':
      case 'settlement_confirmed':
      case 'group_created':
      case 'group_member_joined':
        return 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400';

      case 'expense_updated':
      case 'expense_comment':
      case 'expense_reaction':
      case 'group_updated':
        return 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400';

      case 'expense_deleted':
      case 'settlement_rejected':
      case 'group_deleted':
      case 'group_member_removed':
        return 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400';

      case 'settlement_cancelled':
      case 'group_member_left':
      case 'payment_reminder':
      case 'expense_reminder':
      case 'expense_recurring_paused':
      case 'expense_pick_items':
      case 'group_pro_added':
        return 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400';

      case 'system':
        return 'bg-gray-100 text-gray-600 dark:bg-gray-900/30 dark:text-gray-400';

      default:
        return 'bg-gray-100 text-gray-600 dark:bg-gray-900/30 dark:text-gray-400';
    }
  }
}
